// Data
import topics from "~/data/topics.json";
// Deps
import { getFilteredResources } from "~/modules/wordpress";
import { createComponent } from "react-fela";
import { colors } from "~/styleguide";
import { fluid } from "~/styleguide/utils";
import get from "lodash.get";

// Context / State
import { FilterContext } from "~/context/resource-filter";
import { useContext, useState, useEffect } from "react";
import { DataContext } from "~/context/content";

// Components
import TypeFilter from "./type";
import ProductsFilter from "./products";
import DemoTypesFilter from "./types";
import RevuTopicsFilter from "./revutopics";
import TopicFilter from "./topic";
import SearchBar from "./search";

// Taxonomy base for relational query
const TOPIC_TAX_BASE = { taxonomy: "resource-topics", field: "id" };
const PRODUCT_TAX_BASE = { taxonomy: "product-categories", field: "id" };
const DEMOTYPE_TAX_BASE = { taxonomy: "demo-types", field: "id" };
const REVUTOPICS_TAX_BASE = { taxonomy: "revu-topics", field: "id" };

export default function ResourcesFilter() {
  const showSearchBar = true;
  const metaData = useContext(DataContext);
  const data = get(metaData, "listing_page", metaData);
  const slug = get(data, "slug");

  const hideTypeFilter = ["podcasts","inside-sales-glossary","revu","case-studies","blog"].includes(slug);
  const hideTopicFilter = ["revu","blog","case-studies"].includes(slug);
  const setTopicFilter = slug === "podcasts";
  const showRevUFilter = slug === "revu";

  const filterState = useContext(FilterContext);

  // Initial topic list
  const initialTopics = topics.map(t => ({ value: t.id, label: t.name }));
  const [filteredTopics, setFilteredTopics] = useState(
    setTopicFilter ? initialTopics.filter(t => t.label.includes("Featured")) : []
  );

  const [filteredContentTypes, setFilteredContentTypes] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [filteredDemoTypes, setFilteredDemoTypes] = useState([]);
  const [filteredRevuTopics, setFilteredRevuTopics] = useState([]);
  const [searchString, setSearchString] = useState("");

  // Determine resource types by slug
  const resourceTypeMap = {
    podcasts: [{ value: "podcasts", label: "Podcasts" }],
    "inside-sales-glossary": [{ value: "glossary", label: "Glossary" }],
    "case-studies": [{ value: "casestudies", label: "Case Studies" }],
    blog: [{ value: "post", label: "Blogs" }],
    revu: [{ value: "videos", label: "RevU" }],
  };
  const defaultTypes = [
//    { value: "casestudies", label: "Customer Stories" },
    { value: "whitepapers", label: "eBooks & Guides" },
    { value: "infographics", label: "Infographics" },
//    { value: "podcasts", label: "Podcasts" },
    { value: "tools", label: "Tools" },
//    { value: "webinars", label: "Webinars" },
//    { value: "post", label: "Blog" },
  ];
  const resourceTypes = resourceTypeMap[slug] || defaultTypes;

  // Submit handler
  const handleSubmit = async (event, entries = {}) => {
    if (event) event.preventDefault();

    const actualContentTypes = entries.contentTypes || filteredContentTypes;
    const actualTopics = entries.topics || filteredTopics;
    const actualDemoTypes = entries.demotypes || filteredDemoTypes;
    const actualProducts = entries.products || filteredProducts;
    const actualRevuTopics = entries.revutopics || filteredRevuTopics;

    // Reset if empty
    if (
      !searchString &&
      !actualTopics.length &&
      !actualContentTypes.length &&
      !actualDemoTypes.length &&
      !actualProducts.length &&
      !actualRevuTopics.length
    ) {
      clearFilters(event);
      return;
    }

    filterState.setLoading(true);
    let filterQuery = getFilteredResources()();

    // Build combined tax_query
    const taxQueries = [];
    if (actualTopics.length) {
      const q = { relation: "OR" };
      actualTopics.forEach((t, i) => {
        q[i] = { ...TOPIC_TAX_BASE, terms: [t.value] };
      });
      taxQueries.push(q);
    }
    if (actualProducts.length) {
      const q = { relation: "OR" };
      actualProducts.forEach((p, i) => {
        q[i] = { ...PRODUCT_TAX_BASE, terms: [p.value] };
      });
      taxQueries.push(q);
    }
    if (actualRevuTopics.length) {
      const q = { relation: "OR" };
      actualRevuTopics.forEach((p, i) => {
        q[i] = { ...REVUTOPICS_TAX_BASE, terms: [p.value] };
      });
      taxQueries.push(q);
    }
    if (actualDemoTypes.length) {
      const q = { relation: "OR" };
      actualDemoTypes.forEach((d, i) => {
        q[i] = { ...DEMOTYPE_TAX_BASE, terms: [d.value] };
      });
      taxQueries.push(q);
    }
    if (taxQueries.length) {
      // Outer relation AND across taxonomies
      const combined = { relation: "AND" };
      taxQueries.forEach((sub, idx) => {
        combined[idx] = sub;
      });
      filterQuery = filterQuery.param("tax_query", combined);
    }

    // Post types
    filterQuery = filterQuery.post_type(
      actualContentTypes.length
        ? actualContentTypes.map(ct => ct.value)
        : resourceTypes.map(rt => rt.value)
    );

    // Search
    if (searchString) filterQuery = filterQuery.param("s", searchString);

    try {
      const [resources] = await Promise.all([
        filterQuery.get(),
        new Promise(r => setTimeout(r, 350)),
      ]);
      filterState.setResources(resources);
    } catch (err) {
      console.error("Filter query error:", err);
      filterState.setResources([]);
    } finally {
      filterState.setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (filteredTopics.length) handleSubmit();
  }, []);

  // Change handlers
  const handleTopicChange = sel => {
    setFilteredTopics(sel);
    handleSubmit(null, { topics: sel });
  };
  const handleTypeChange = sel => {
    setFilteredContentTypes(sel);
    handleSubmit(null, { contentTypes: sel });
  };
  const handleProductsChange = sel => {
    setFilteredProducts(sel);
    handleSubmit(null, { products: sel });
  };
  const handleDemoTypesChange = sel => {
    setFilteredDemoTypes(sel);
    handleSubmit(null, { demotypes: sel });
  };
  const handleRevuTopicsChange = sel => {
    setFilteredRevuTopics(sel);
    handleSubmit(null, { revutopics: sel });
  };
  const handleSearchChange = e => setSearchString(e.target.value);

  const clearFilters = event => {
    if (event) event.preventDefault();
    setFilteredTopics([]);
    setFilteredContentTypes([]);
    setFilteredProducts([]);
    setFilteredDemoTypes([]);
    setFilteredRevuTopics([]);
    setSearchString("");
    filterState.setResources(null);
  };

  return (
    <form onSubmit={handleSubmit}>
      <FlexRow isCentered={!showSearchBar || slug === "podcasts" || slug === "revu"}>
        {showRevUFilter && (
          <ProductsFilter
            handleProductsChange={handleProductsChange}
            selected={filteredProducts}
          />
        )}
        {showRevUFilter && (
          <DemoTypesFilter
            handleDemoTypesChange={handleDemoTypesChange}
            selected={filteredDemoTypes}
          />
        )}
        {showRevUFilter && (
          <RevuTopicsFilter
            handleRevuTopicsChange={handleRevuTopicsChange}
            selected={filteredRevuTopics}
          />
        )}
        {!hideTopicFilter && (
          <TopicFilter
            handleTopicChange={handleTopicChange}
            selected={filteredTopics}
          />
        )}
        {!hideTypeFilter && (
          <TypeFilter
            handleTypeChange={handleTypeChange}
            options={resourceTypes}
            selected={filteredContentTypes}
          />
        )}
        {showSearchBar && (
          <Container isExtended={slug === "inside-sales-glossary" || slug === "blog"}>
            <SearchBar handleSearchChange={handleSearchChange} value={searchString}>
              <Button
                type="submit"
                extend={{
                  position: "absolute",
                  top: 2,
                  right: 0,
                  appearance: "none",
                  border: "none",
                  backgroundColor: "transparent",
                  backgroundImage: `url("${searchSVG()}")`,
                  height: "54px",
                  width: "54px",
                  backgroundPosition: "center center",
                  backgroundRepeat: "no-repeat",
                  whenActive: { backgroundImage: `url("${searchSVG(colors.blue)}")` },
                }}
              />
            </SearchBar>
          </Container>
        )}
        <Button onClick={clearFilters} extend={{
          color: colors.blue,
          background: colors.white,
          border: `1px solid ${colors.blue}`,
          width: 167,
          ":hover": { background: colors.blue, color: colors.white, borderColor: colors.blue },
          maxWidthMobile: { width: "100%" },
        }}>
          Clear
        </Button>
      </FlexRow>
    </form>
  );
}

const searchSVG = (color = colors.black) => {
  color = escape(color);
  return `data:image/svg+xml,%3Csvg width='32' height='32' viewBox='0 0 32 32' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M12.6667 21.3333C17.4531 21.3333 21.3333 17.4531 21.3333 12.6667C21.3333 7.8802 17.4531 4 12.6667 4C7.8802 4 4 7.8802 4 12.6667C4 17.4531 7.8802 21.3333 12.6667 21.3333Z' stroke='${color}'/%3E%3Cpath d='M20.6804 21.6037L27.2801 28.2033' stroke='${color}' stroke-linecap='round'/%3E%3C/svg%3E%0A`;
};

const FlexRow = createComponent(({ isCentered }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
  maxWidthTabletLandscape: { flexWrap: "wrap" },
  maxWidthMobile: { justifyContent: "center" },
  "> div": { marginRight: 20 },
  extend: [
    fluid("padding-bottom", { max: 50, min: 30 }),
    { condition: isCentered, style: { minWidthTabletLandscape: { justifyContent: "center" }, maxWidthTabletLandscape: { justifyContent: "flex-end" } } }
  ]
}));

const Container = createComponent(() => ({
  flexGrow: 2,
  position: "relative",
  maxWidthTabletLandscape: { width: "calc(100% - 187px)" },
  maxWidthMobile: { marginRight: "0 !important", marginBottom: 20, width: "100%" },
}));

const Button = createComponent(
  () => ({ height: 54, appearance: "none", borderRadius: 6, outline: 0, userSelect: "none", cursor: "pointer", paddingH: 16, transition: "color 0.2s ease, border-color 0.2s ease, background 0.2s ease" }),
  "button",
  ["onClick"]
);
